import { create } from 'zustand';
import type { AlternativeCategoryFilter, AlternativeHistoryWindow, AlternativeLinkCommitResult } from '../services/costPrice/alternativeLinkTypes';
import type { AlternativeReviewState } from '../components/pages/costPrice/new/CostPrice/alternativeLinkReview';

export interface AlternativeLinkSession {
  id: number;
  state: AlternativeReviewState | null;
  window: AlternativeHistoryWindow;
  categoryFilter: AlternativeCategoryFilter;
  categories: string[];
  busy: 'scan' | 'apply' | 'refresh' | null;
  error: string | null;
  reloadFailed: boolean;
  results: AlternativeLinkCommitResult[];
  controller: AbortController | null;
}

interface AlternativeLinkReviewStore {
  sessions: Record<string, AlternativeLinkSession>;
  ensure: (restaurantId: string) => AlternativeLinkSession;
  update: (restaurantId: string, sessionId: number, fn: (session: AlternativeLinkSession) => AlternativeLinkSession) => void;
  reset: (restaurantId: string) => void;
  clear: () => void;
}

let nextSessionId = 0;
const emptySession = (): AlternativeLinkSession => ({
  id: ++nextSessionId, state: null, window: 'two_months', categoryFilter: 'all', categories: [], busy: null, error: null,
  reloadFailed: false, results: [], controller: null,
});

/** In-memory drafts survive drawer/page unmounts, but never page reloads or account changes. */
export const useAlternativeLinkReviewStore = create<AlternativeLinkReviewStore>((set, get) => ({
  sessions: {},
  ensure: restaurantId => {
    const existing = get().sessions[restaurantId];
    if (existing) return existing;
    const session = emptySession();
    set(state => ({ sessions: { ...state.sessions, [restaurantId]: session } }));
    return session;
  },
  update: (restaurantId, sessionId, fn) => {
    const session = get().sessions[restaurantId];
    // A late scan/commit must never resurrect a reset session or touch another restaurant.
    if (!session || session.id !== sessionId) return;
    set(state => ({ sessions: { ...state.sessions, [restaurantId]: fn(session) } }));
  },
  reset: restaurantId => {
    const session = get().sessions[restaurantId];
    if (session?.busy === 'apply') return;
    session?.controller?.abort();
    set(state => ({ sessions: { ...state.sessions, [restaurantId]: emptySession() } }));
  },
  clear: () => {
    Object.values(get().sessions).forEach(session => session.controller?.abort());
    set({ sessions: {} });
  },
}));
